import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

// Tipler (SizeSpecificationsPage ile aynı)
interface SizeMeasurement {
  size: string;
  [measurement: string]: number | string;
}

interface TechnicalSpec {
  sizeSpecs?: SizeMeasurement[];
}

interface Product {
  _id: string;
  modelName: string;
  productCode?: string;
  technicalSpec?: TechnicalSpec;
}

const SizeSpecDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>(); // URL'den productId'yi al
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchProductSpec = async () => {
      if (!productId) {
        setError('Ürün ID bulunamadı.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        // Sadece ilgili ürünü ve gerekli alanları çek
        const response = await axios.get(`http://localhost:8012/api/products/${productId}?fields=name,productCode,technicalSpec`);
        const fetchedProduct: Product = response.data;

        // Gelen üründe sizeSpecs var mı kontrol et
        if (!fetchedProduct.technicalSpec?.sizeSpecs || fetchedProduct.technicalSpec.sizeSpecs.length === 0) {
          setError('Bu ürün için ölçü tablosu verisi bulunamadı.');
          setProduct(fetchedProduct); // Ürün bilgisini yine de sakla (başlık için)
        } else {
          setProduct(fetchedProduct);
        }

      } catch (err: any) {
        console.error("Error fetching product spec:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Ölçü tablosu yüklenirken bir hata oluştu.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProductSpec();
  }, [productId]); // productId değiştiğinde tekrar fetch et

  // Tablo başlıklarını oluşturma fonksiyonu (SizeSpecificationsPage'den alındı)
  const getTableHeaders = (specs: SizeMeasurement[]): string[] => {
    if (!specs || specs.length === 0) return ['Beden'];
    const allMeasurementPoints = new Set<string>();
    specs.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'size' && key !== '_id') {
          allMeasurementPoints.add(key);
        }
      });
    });
    const sortedMeasurements = Array.from(allMeasurementPoints).sort();
    return ['Beden', ...sortedMeasurements];
  };

  const sizeSpecsData = product?.technicalSpec?.sizeSpecs;
  const headers = sizeSpecsData ? getTableHeaders(sizeSpecsData) : [];

  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      {/* Başlık */}
      <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
             <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {product ? `${product.modelName} - Ölçü Tablosu` : 'Ölçü Tablosu Detayı'}
        </h1>
        <Link to="/size-specifications" className="text-sm text-blue-600 hover:underline">
          &larr; Listeye Geri Dön
        </Link>
      </div>

      {/* Yükleniyor durumu */}
      {loading && (
        <div className="flex justify-center items-center p-10">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mr-4 border-t-purple-600 animate-spin"></div>
          <span className="text-gray-500">Yükleniyor...</span>
        </div>
      )}

      {/* Hata durumu */}
      {error && !loading && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm animate-fade-in" role="alert">
          <p>{error}</p>
          {/* Ürün bilgisi varsa, ürün detayına link verilebilir */}
          {product && (
             <p className="mt-2">
                <Link to={`/products/${product._id}`} className="font-semibold hover:underline">
                   "{product.name}" ürün detayına gitmek için tıklayın.
                </Link>
             </p>
          )}
        </div>
      )}

      {/* Ölçü Tablosu */}
      {!loading && !error && product && sizeSpecsData && headers.length > 1 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead className="bg-gray-200">
              <tr>
                {headers.map((header) => (
                  <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    {header.charAt(0).toUpperCase() + header.slice(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sizeSpecsData.map((sizeItem, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">{sizeItem.size}</td>
                  {headers.slice(1).map(measurementHeader => (
                    <td key={measurementHeader} className="px-4 py-2 whitespace-nowrap text-gray-700 text-center">
                      {sizeItem[measurementHeader] !== undefined ? sizeItem[measurementHeader] : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
       {/* Stil Tanımları */}
       <style jsx>{`
        .loader {
          animation: spinner 1.5s linear infinite;
        }
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SizeSpecDetailPage;
